﻿const NAMED_COLORS = {
    black: "#000000",
    white: "#FFFFFF",
    red: "#FF0000",
    green: "#008000",
    blue: "#0000FF",
    yellow: "#FFFF00",
    orange: "#FFA500",
    purple: "#800080",
    pink: "#FFC0CB",
    gray: "#808080",
    grey: "#808080",
    indigo: "#4F46E5",
    cyan: "#00FFFF"
};

export function parseColorInput(input) {
    if (!input) return null;

    const raw = String(input).trim();

    const hex = normalizeHex(raw);
    if (hex) return hex;

    const named = NAMED_COLORS[raw.toLowerCase()];
    if (named) return named;

    const rgbMatch = raw.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})(?:\s*,\s*([0-9.]+))?\s*\)$/i);
    if (rgbMatch) {
        const r = clamp255(Number(rgbMatch[1]));
        const g = clamp255(Number(rgbMatch[2]));
        const b = clamp255(Number(rgbMatch[3]));
        return rgbToHex(r, g, b);
    }

    const hslMatch = raw.match(/^hsla?\(\s*(-?\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)%\s*,\s*(\d+(?:\.\d+)?)%(?:\s*,\s*([0-9.]+))?\s*\)$/i);
    if (hslMatch) {
        const h = normalizeHue(Number(hslMatch[1]));
        const s = clampPercent(Number(hslMatch[2]));
        const l = clampPercent(Number(hslMatch[3]));
        const rgb = hslToRgb(h, s, l);
        return rgbToHex(rgb.r, rgb.g, rgb.b);
    }

    return null;
}

function clamp255(v) {
    return Math.max(0, Math.min(255, Math.round(v)));
}

function clampPercent(v) {
    return Math.max(0, Math.min(100, v));
}

function normalizeHue(v) {
    return ((v % 360) + 360) % 360;
}

export function normalizeHex(input) {
    if (!input) return null;

    let value = String(input).trim().replace("#", "");

    if (/^[0-9a-f]{3}$/i.test(value)) {
        value = value.split("").map(x => x + x).join("");
    }

    if (!/^[0-9a-f]{6}$/i.test(value)) {
        return null;
    }

    return `#${value.toUpperCase()}`;
}


export function hexToRgb(hex) {
    const normalized = normalizeHex(hex);
    if (!normalized) return null;

    const value = normalized.slice(1);
    const r = parseInt(value.slice(0, 2), 16);
    const g = parseInt(value.slice(2, 4), 16);
    const b = parseInt(value.slice(4, 6), 16);

    return { r, g, b };
}

export function rgbToHex(r, g, b) {
    const toHex = (v) => Math.max(0, Math.min(255, v)).toString(16).padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

export function rgbToHsl(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);

    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
        }

        h /= 6;
    }

    return {
        h: Math.round(h * 360),
        s: Math.round(s * 100),
        l: Math.round(l * 100)
    };
}

export function hslToRgb(h, s, l) {
    h /= 360;
    s /= 100;
    l /= 100;

    if (s === 0) {
        const gray = Math.round(l * 255);
        return { r: gray, g: gray, b: gray };
    }

    const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    const r = hue2rgb(p, q, h + 1 / 3);
    const g = hue2rgb(p, q, h);
    const b = hue2rgb(p, q, h - 1 / 3);

    return {
        r: Math.round(r * 255),
        g: Math.round(g * 255),
        b: Math.round(b * 255)
    };
}

export function mixColors(hex1, hex2, weight = 0.5) {
    const c1 = hexToRgb(hex1);
    const c2 = hexToRgb(hex2);

    const r = Math.round(c1.r * (1 - weight) + c2.r * weight);
    const g = Math.round(c1.g * (1 - weight) + c2.g * weight);
    const b = Math.round(c1.b * (1 - weight) + c2.b * weight);

    return rgbToHex(r, g, b);
}

export function generateShadeScale(baseHex, scale = "tailwind") {
    const steps = scale === "100"
        ? [100, 200, 300, 400, 500, 600, 700, 800, 900]
        : [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

    const weights = {
        50: 0.92,
        100: 0.84,
        200: 0.68,
        300: 0.48,
        400: 0.22,
        500: 0,
        600: 0.14,
        700: 0.28,
        800: 0.46,
        900: 0.64
    };

    return steps.map(step => {
        if (step === 500) {
            return { step, color: normalizeHex(baseHex) };
        }

        if (step < 500) {
            return {
                step,
                color: mixColors(baseHex, "#FFFFFF", weights[step])
            };
        }

        return {
            step,
            color: mixColors(baseHex, "#000000", weights[step])
        };
    });
}

export function generatePalette(baseHex, type = "analogous", count = 5) {
    const rgb = hexToRgb(baseHex);
    const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

    const results = [];
    const safeCount = Math.max(2, Math.min(8, Number(count || 5)));

    const pushColor = (h, s = hsl.s, l = hsl.l) => {
        const normalizedHue = ((h % 360) + 360) % 360;
        const rgbValue = hslToRgb(normalizedHue, s, l);
        results.push(rgbToHex(rgbValue.r, rgbValue.g, rgbValue.b));
    };

    switch (type) {
        case "complementary":
            pushColor(hsl.h);
            pushColor(hsl.h + 180);
            break;

        case "triadic":
            pushColor(hsl.h);
            pushColor(hsl.h + 120);
            pushColor(hsl.h + 240);
            break;

        case "tetradic":
            pushColor(hsl.h);
            pushColor(hsl.h + 90);
            pushColor(hsl.h + 180);
            pushColor(hsl.h + 270);
            break;

        case "monochromatic":
            for (let i = 0; i < safeCount; i++) {
                const lightness = Math.max(10, Math.min(90, hsl.l - 24 + i * (48 / Math.max(1, safeCount - 1))));
                pushColor(hsl.h, hsl.s, Math.round(lightness));
            }
            break;

        case "analogous":
        default:
            for (let i = 0; i < safeCount; i++) {
                const offset = (i - Math.floor(safeCount / 2)) * 24;
                pushColor(hsl.h + offset);
            }
            break;
    }

    return Array.from(new Set(results));
}

export function buildGradientCss(type, angle, start, end) {
    if (type === "radial") {
        return `background: radial-gradient(circle, ${start} 0%, ${end} 100%);`;
    }

    return `background: linear-gradient(${angle}deg, ${start} 0%, ${end} 100%);`;
}

export function luminanceFromHex(hex) {
    const rgb = hexToRgb(hex);
    const channels = [rgb.r, rgb.g, rgb.b].map(v => {
        const s = v / 255;
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

export function contrastRatio(foregroundHex, backgroundHex) {
    const l1 = luminanceFromHex(foregroundHex);
    const l2 = luminanceFromHex(backgroundHex);

    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);

    return Number(((lighter + 0.05) / (darker + 0.05)).toFixed(2));
}

export function contrastRating(ratio) {
    if (ratio >= 7) {
        return "AAA";
    }
    if (ratio >= 4.5) {
        return "AA";
    }
    if (ratio >= 3) {
        return "AA Large";
    }
    return "Fail";
}

export function rgbaFromHex(hex, alpha = 1) {
    const rgb = hexToRgb(hex);
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

export function buildShadowCss(x, y, blur, spread, colorHex, opacity) {
    const rgba = rgbaFromHex(colorHex, opacity);
    return `box-shadow: ${x}px ${y}px ${blur}px ${spread}px ${rgba};`;
}

export function buildCssVariables(prefix, shades) {
    const lines = [":root {"];

    shades.forEach(item => {
        lines.push(`  --${prefix}-${item.step}: ${item.color};`);
    });

    lines.push("}");
    return lines.join("\n");
}

export function buildScssVariables(prefix, shades) {
    return shades
        .map(item => `$${prefix}-${item.step}: ${item.color};`)
        .join("\n");
}

export function buildJsonScale(shades) {
    const obj = {};
    shades.forEach(item => {
        obj[item.step] = item.color;
    });

    return JSON.stringify(obj, null, 2);
}

export function buildTailwindLikeScale(shades) {
    const obj = {};
    shades.forEach(item => {
        obj[item.step] = item.color;
    });

    return JSON.stringify(obj, null, 2);
}

export function buildUtilityClasses(prefix, shades) {
    const lines = [];

    shades.forEach(item => {
        lines.push(`.bg-${prefix}-${item.step} { background-color: var(--${prefix}-${item.step}); }`);
        lines.push(`.text-${prefix}-${item.step} { color: var(--${prefix}-${item.step}); }`);
        lines.push(`.border-${prefix}-${item.step} { border-color: var(--${prefix}-${item.step}); }`);
        lines.push("");
    });

    return lines.join("\n").trim();
}

export function buildSemanticTokens(name, shades) {
    const get = (step) => shades.find(x => x.step === step)?.color || shades[0]?.color || "#000000";

    const lines = [
        ":root {",
        `  --${name}: ${get(500)};`,
        `  --${name}-foreground: ${get(50)};`,
        `  --${name}-hover: ${get(600)};`,
        `  --${name}-active: ${get(700)};`,
        `  --${name}-soft: ${get(100)};`,
        `  --${name}-soft-hover: ${get(200)};`,
        `  --${name}-border: ${get(300)};`,
        `  --${name}-strong: ${get(800)};`,
        "}"
    ];

    return lines.join("\n");
}

export function randomHexColor() {
    const value = Math.floor(Math.random() * 0xffffff)
        .toString(16)
        .padStart(6, "0");

    return `#${value}`.toUpperCase();
}

export function buildOpacityScale(baseHex, steps = [5, 10, 20, 30, 40, 50, 60, 70, 80, 90]) {
    const rgb = hexToRgb(baseHex);

    return steps.map(step => ({
        step,
        value: `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${(step / 100).toFixed(2)})`
    }));
}

export function bestContrastText(hex) {
    const whiteRatio = contrastRatio("#FFFFFF", hex);
    const blackRatio = contrastRatio("#000000", hex);

    return whiteRatio >= blackRatio ? "#FFFFFF" : "#000000";
}

export function buildTailwindConfig(prefix, shades) {
    const lines = [
        "export default {",
        "  theme: {",
        "    extend: {",
        "      colors: {",
        `        ${prefix}: {`
    ];

    shades.forEach(item => {
        lines.push(`          ${item.step}: "${item.color}",`);
    });

    lines.push("        }");
    lines.push("      }");
    lines.push("    }");
    lines.push("  }");
    lines.push("};");

    return lines.join("\n");
}

export function buildThemePack(baseHex, name = "brand") {
    const shades = generateShadeScale(baseHex, "tailwind");
    const get = (step) => shades.find(x => x.step === step)?.color || baseHex;

    const success = "#10B981";
    const warning = "#F59E0B";
    const danger = "#EF4444";
    const neutral = "#6B7280";

    return [
        ":root {",
        `  --${name}: ${get(500)};`,
        `  --${name}-foreground: ${bestContrastText(get(500))};`,
        `  --${name}-hover: ${get(600)};`,
        `  --${name}-active: ${get(700)};`,
        `  --${name}-soft: ${get(100)};`,
        `  --${name}-soft-hover: ${get(200)};`,
        `  --${name}-border: ${get(300)};`,
        `  --${name}-strong: ${get(800)};`,
        "",
        `  --success: ${success};`,
        `  --success-foreground: ${bestContrastText(success)};`,
        `  --warning: ${warning};`,
        `  --warning-foreground: ${bestContrastText(warning)};`,
        `  --danger: ${danger};`,
        `  --danger-foreground: ${bestContrastText(danger)};`,
        `  --neutral: ${neutral};`,
        `  --neutral-foreground: ${bestContrastText(neutral)};`,
        "",
        `  --surface: #FFFFFF;`,
        `  --surface-soft: #F8FAFC;`,
        `  --surface-muted: #EEF2F7;`,
        `  --text: #0F172A;`,
        `  --text-muted: #475569;`,
        `  --border: #E2E8F0;`,
        "}"
    ].join("\n");
}

export function generateShadeScaleHsl(baseHex, scale = "tailwind") {
    const steps = scale === "100"
        ? [100, 200, 300, 400, 500, 600, 700, 800, 900]
        : [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

    const rgb = hexToRgb(baseHex);
    const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

    const lightnessMap = {
        50: 96,
        100: 92,
        200: 84,
        300: 72,
        400: 60,
        500: hsl.l,
        600: 50,
        700: 40,
        800: 28,
        900: 18
    };

    return steps.map(step => {
        const l = step === 500 ? hsl.l : lightnessMap[step];
        const rgbValue = hslToRgb(hsl.h, hsl.s, l);

        return {
            step,
            color: rgbToHex(rgbValue.r, rgbValue.g, rgbValue.b)
        };
    });
}

function srgbToLinear(c) {
    c /= 255;
    return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function linearToSrgb(c) {
    const v = c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
    return Math.round(Math.max(0, Math.min(1, v)) * 255);
}

function rgbToOklab(r, g, b) {
    const lr = srgbToLinear(r);
    const lg = srgbToLinear(g);
    const lb = srgbToLinear(b);

    const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
    const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
    const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;

    const l_ = Math.cbrt(l);
    const m_ = Math.cbrt(m);
    const s_ = Math.cbrt(s);

    return {
        L: 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_,
        a: 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_,
        b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_
    };
}

function oklabToRgb(L, a, b) {
    const l_ = Math.pow(L + 0.3963377774 * a + 0.2158037573 * b, 3);
    const m_ = Math.pow(L - 0.1055613458 * a - 0.0638541728 * b, 3);
    const s_ = Math.pow(L - 0.0894841775 * a - 1.2914855480 * b, 3);

    const lr = +4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_;
    const lg = -1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_;
    const lb = -0.0041960863 * l_ - 0.7034186147 * m_ + 1.7076147010 * s_;

    return {
        r: linearToSrgb(lr),
        g: linearToSrgb(lg),
        b: linearToSrgb(lb)
    };
}

function oklabToOklch(L, a, b) {
    return {
        L,
        C: Math.sqrt(a * a + b * b),
        h: Math.atan2(b, a)
    };
}

function oklchToOklab(L, C, h) {
    return {
        L,
        a: C * Math.cos(h),
        b: C * Math.sin(h)
    };
}

export function generateShadeScaleOklch(baseHex, scale = "tailwind") {
    const steps = scale === "100"
        ? [100, 200, 300, 400, 500, 600, 700, 800, 900]
        : [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

    const rgb = hexToRgb(baseHex);
    const lab = rgbToOklab(rgb.r, rgb.g, rgb.b);
    const lch = oklabToOklch(lab.L, lab.a, lab.b);

    const lightnessMap = {
        50: 0.97,
        100: 0.93,
        200: 0.86,
        300: 0.76,
        400: 0.67,
        500: lch.L,
        600: Math.max(0.56, lch.L - 0.08),
        700: Math.max(0.46, lch.L - 0.16),
        800: Math.max(0.34, lch.L - 0.26),
        900: Math.max(0.24, lch.L - 0.36)
    };

    return steps.map(step => {
        const targetL = step === 500 ? lch.L : lightnessMap[step];

        // chroma'yı uç tonlarda biraz azalt
        const chromaFactor =
            step <= 100 ? 0.35 :
                step <= 200 ? 0.55 :
                    step <= 300 ? 0.75 :
                        step <= 400 ? 0.9 :
                            step === 500 ? 1 :
                                step <= 600 ? 0.92 :
                                    step <= 700 ? 0.82 :
                                        step <= 800 ? 0.68 :
                                            0.52;

        const next = oklchToOklab(targetL, lch.C * chromaFactor, lch.h);
        const rgbValue = oklabToRgb(next.L, next.a, next.b);

        return {
            step,
            color: rgbToHex(rgbValue.r, rgbValue.g, rgbValue.b)
        };
    });
}